/**
 * Viewport top of `element` as laid out, leaving out the transforms applied to it and to its
 * ancestors up to `container`.
 *
 * Messages rise into place with a `translateY`, and the chat pins them by nodes inside the
 * rising one: measured transformed, a pin lands that far off once the message has settled.
 * Translations are undone all the way up; a scale only on the element itself, since an
 * entrance only ever scales small leaves (buttons, cards), never what holds a pinned node.
 */
export const getLayoutTop = (element: HTMLElement, container?: HTMLElement | null): number => {
  let top = element.getBoundingClientRect().top;
  if (typeof DOMMatrixReadOnly === "undefined") return top;

  for (
    let node: HTMLElement | null = element;
    node && node !== container && node !== document.body;
    node = node.parentElement
  ) {
    const { transform, transformOrigin } = window.getComputedStyle(node);
    if (!transform || transform === "none") continue;

    const { d: scaleY, f: translateY } = new DOMMatrixReadOnly(transform);
    top -= translateY;
    if (node === element) {
      const originY = parseFloat(transformOrigin.split(" ")[1] ?? "0") || 0;
      top -= originY * (1 - scaleY);
    }
  }

  return top;
};

/**
 * Scrolls `container` so that `target`'s top aligns with the container's top.
 *
 * Implemented with `Element.scrollTo` (not `scrollIntoView`) on purpose: when
 * the chat runs inside an iframe, `scrollIntoView` propagates the scroll
 * request to every scrollable ancestor — including the parent document — which
 * causes the host page to scroll as well. Computing the offset manually and
 * scrolling only the messages viewport keeps the scroll contained in the iframe.
 *
 * The target is measured without the entrance transform of the message holding it, so a
 * message still rising into place is pinned where it will settle.
 */
export const scrollElementToTop = (
  container: HTMLElement,
  target: HTMLElement,
  behavior: ScrollBehavior = "auto"
) => {
  const containerRect = container.getBoundingClientRect();
  const top = container.scrollTop + (getLayoutTop(target, container) - containerRect.top);
  container.scrollTo({ top, behavior });
};

/**
 * Scroll offset of `container` that brings the end of `content` into view, as laid out.
 *
 * `scrollHeight` also counts the overflow of a message still rising into place, which then
 * shrinks as it rises: scrolling to it, the browser clamps back frame by frame and eats the
 * rise whenever the list's bottom padding is shorter than the travel.
 */
export const getLayoutScrollBottom = (container: HTMLElement, content: HTMLElement): number =>
  Math.max(
    0,
    container.scrollTop +
      (content.getBoundingClientRect().top - container.getBoundingClientRect().top) +
      content.offsetHeight -
      container.clientHeight
  );
